import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    updateDoc,
    where,
    Timestamp,
    DocumentReference
} from 'firebase/firestore';
import { db } from '@/firebase';
import { formatPhoneNumber } from '../../../utils/phone';

interface UserData {
    id: string;
    [key: string]: any;
}

interface OrderSummary {
    id: string;
    status?: string;
    orderNumber?: string;
    createdAt?: any;
    totalAmount?: number;
    shikakuId?: DocumentReference | string;
    shikakuName?: string;
}

interface ShikakuOption {
    id: string;
    ShikakuName: string;
}

const FIELD_LABELS: Record<string, string> = {
    id: 'ユーザーID',
    display_name: '表示名',
    email: 'メールアドレス',
    phoneNumber: '電話番号',
    Role: '権限',
    postalCode: '郵便番号',
    address: '住所',
    postalC: '郵便番号（請求先）',
    AddressC: '住所（請求先）',
    billingPostalCode: '請求先郵便番号',
    billingAddress: '請求先住所',
    sfId: 'sfId',
    sfid: 'sfId',
    ssid: 'SSID',
    createdAt: '作成日時',
    updatedAt: '更新日時',
};

const EDITABLE_FIELDS = [
    'display_name',
    'email',
    'phoneNumber',
    'Role',
    'postalCode',
    'address',
    'postalC',
    'AddressC',
    'billingPostalCode',
    'billingAddress',
];

const LEFT_FIELDS = [
    { key: 'id', label: 'ユーザーID' },
    { key: 'display_name', label: '表示名' },
    { key: 'phoneNumber', label: '電話番号' },
    { key: 'email', label: 'メールアドレス' },
    { key: 'createdAt', label: '作成日時' },
];

const RIGHT_FIELDS = [
    { key: 'sfId', label: 'sfId', fallbackKey: 'sfid' },
    { key: 'Role', label: '権限' },
    { key: 'postalCode', label: '郵便番号' },
    { key: 'address', label: '住所' },
];

const SUPPLEMENTAL_FIELDS = [
    { key: 'postalC', label: '郵便番号（請求先）' },
    { key: 'AddressC', label: '住所（請求先）' },
];

const normalizeValue = (value: any, key?: string, seen: WeakSet<object> = new WeakSet()): any => {
    if (value === null || value === undefined) return value;

    if (value instanceof Timestamp) {
        return value.toDate().toLocaleString('ja-JP');
    }

    if (value instanceof DocumentReference) {
        return value.path || value.id;
    }

    if (typeof value === 'string' && key?.toLowerCase().includes('phone')) {
        return formatPhoneNumber(value);
    }

    if (typeof value === 'object') {
        if (seen.has(value)) {
            return '[Circular]';
        }
        seen.add(value);

        if (Array.isArray(value)) {
            return value.map((item) => normalizeValue(item, undefined, seen));
        }

        return Object.fromEntries(
            Object.entries(value).map(([childKey, val]) => [childKey, normalizeValue(val, childKey, seen)])
        );
    }

    return value;
};

export default function UserDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [userData, setUserData] = useState<UserData | null>(null);
    const [editValues, setEditValues] = useState<Partial<UserData>>({});
    const [orders, setOrders] = useState<OrderSummary[]>([]);
    const [shikakuList, setShikakuList] = useState<ShikakuOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isAddingOrder, setIsAddingOrder] = useState(false);
    const [addingOrder, setAddingOrder] = useState(false);
    const [newOrderForm, setNewOrderForm] = useState({
        shikakuId: '',
        status: 'active',
        course: '通信講座＋試験',
        diploma: '通常版',
        paymentMethod: 'クレジット',
        totalAmount: 0,
        isExamFinished: false
    });
    const [error, setError] = useState<string | null>(null);


    const resolveShikakuName = async (shikakuValue?: DocumentReference | string) => {
        if (!shikakuValue) return '-';

        try {
            const shikakuRef = typeof shikakuValue === 'string'
                ? doc(db, 'Shikaku', shikakuValue)
                : shikakuValue;
            const shikakuSnapshot = await getDoc(shikakuRef);
            if (!shikakuSnapshot.exists()) {
                return '-';
            }
            return shikakuSnapshot.data().ShikakuName || shikakuSnapshot.id;
        } catch (error) {
            console.error('Error resolving shikaku name:', error);
            return '-';
        }
    };

    const loadOrdersByUser = async (userId: string) => {
        const userRef = doc(db, 'User', userId);
        const orderQuery = query(collection(db, 'Order'), where('UserRef', '==', userRef));
        const ordersSnapshot = await getDocs(orderQuery);

        const orderData = await Promise.all(
            ordersSnapshot.docs.map(async (docItem) => {
                const data = docItem.data() as OrderSummary;
                const shikakuName = await resolveShikakuName(data.shikakuId);
                return {
                    id: docItem.id,
                    ...data,
                    shikakuName,
                } as OrderSummary;
            })
        );

        setOrders(orderData);
    };

    const fetchShikaku = async () => {
        try {
            const shikakuSnapshot = await getDocs(collection(db, 'Shikaku'));
            const shikakuData = shikakuSnapshot.docs.map((docItem) => ({
                id: docItem.id,
                ShikakuName: docItem.data().ShikakuName || docItem.id,
            }));
            setShikakuList(shikakuData);
        } catch (err) {
            console.error('Error fetching shikaku:', err);
        }
    };

    const generateOrderNumber = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        return `ORD-${year}${month}${day}-${random}`;
    };

    const resetOrderForm = () => {
        setNewOrderForm({
            shikakuId: '',
            status: 'active',
            course: '通信講座＋試験',
            diploma: '通常版',
            paymentMethod: 'クレジット',
            totalAmount: 0,
            isExamFinished: false,
        });
    };

    const handleAddOrder = async () => {
        if (!id || addingOrder) return;
        if (!newOrderForm.shikakuId) {
            alert('資格を選択してください');
            return;
        }

        setAddingOrder(true);
        try {
            const createdAt = Timestamp.now();
            const expiresAt = Timestamp.fromDate(
                new Date(createdAt.toDate().getTime() + 2 * 365 * 24 * 60 * 60 * 1000)
            );

            const shikakuRef = doc(db, 'Shikaku', newOrderForm.shikakuId);
            let examSetRef: any = null;

            const latestActiveSetQuery = query(
                collection(db, 'ExamSets'),
                where('shikakuRef', '==', shikakuRef),
                where('effectiveFrom', '<=', createdAt),
                orderBy('effectiveFrom', 'desc'),
                limit(1)
            );
            const latestActiveSetSnapshot = await getDocs(latestActiveSetQuery);
            if (!latestActiveSetSnapshot.empty) {
                examSetRef = latestActiveSetSnapshot.docs[0].ref;
            } else {
                const v0Query = query(
                    collection(db, 'ExamSets'),
                    where('shikakuRef', '==', shikakuRef),
                    where('version', '==', 0),
                    limit(1)
                );
                const v0Snapshot = await getDocs(v0Query);
                if (!v0Snapshot.empty) {
                    examSetRef = v0Snapshot.docs[0].ref;
                }
            }

            await addDoc(collection(db, 'Order'), {
                UserRef: doc(db, 'User', id),
                shikakuId: shikakuRef,
                examSetRef,
                orderNumber: generateOrderNumber(),
                createdAt,
                expiresAt,
                status: newOrderForm.status,
                course: newOrderForm.course,
                diploma: newOrderForm.diploma,
                paymentMethod: newOrderForm.paymentMethod,
                totalAmount: Number(newOrderForm.totalAmount) || 0,
                isExamFinished: newOrderForm.isExamFinished,
                retest1: false,
                retest2: false,
                trouble: false,
                lost: false,
            });

            alert('Orderを追加しました');
            setIsAddingOrder(false);
            resetOrderForm();
            await loadOrdersByUser(id);
        } catch (err: any) {
            console.error('Error adding order:', err);
            alert(err?.message || 'Order追加に失敗しました');
        } finally {
            setAddingOrder(false);
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
            if (!id) {
                setError('ユーザーIDが指定されていません');
                setLoading(false);
                return;
            }

            try {
                const userRef = doc(db, 'User', id);
                const snapshot = await getDoc(userRef);

                if (!snapshot.exists()) {
                    setError('ユーザーが見つかりませんでした');
                    setLoading(false);
                    return;
                }

                const data = { id: snapshot.id, ...snapshot.data() } as UserData;
                setUserData(data);
                setEditValues(data);

                await loadOrdersByUser(snapshot.id);
            } catch (err: any) {
                console.error('Error loading user detail:', err);
                setError(err?.message || 'ユーザー情報の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id]);

    useEffect(() => {
        fetchShikaku();
    }, []);

    const normalizedUserData = useMemo(() => {
        if (!userData) return null;
        return normalizeValue(userData);
    }, [userData]);

    const handleSave = async () => {
        if (!id || saving) return;
        setSaving(true);

        try {
            const updates: Record<string, any> = {};
            EDITABLE_FIELDS.forEach((field) => {
                if (editValues[field] !== undefined) {
                    updates[field] = editValues[field];
                }
            });

            await updateDoc(doc(db, 'User', id), updates);
            setUserData((prev) => (prev ? { ...prev, ...updates } : prev));
            alert('ユーザー情報を更新しました');
        } catch (err: any) {
            console.error('Error saving user:', err);
            alert(err?.message || 'ユーザー情報の更新に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    const renderField = (field: { key: string; label?: string; fallbackKey?: string }) => {
        const resolvedKey = userData && field.fallbackKey && !(field.key in userData) && field.fallbackKey in userData
            ? field.fallbackKey
            : field.key;
        const label = field.label || FIELD_LABELS[resolvedKey] || resolvedKey;
        const isEditable = EDITABLE_FIELDS.includes(resolvedKey);
        const value = isEditable ? editValues[resolvedKey] ?? '' : normalizedUserData?.[resolvedKey];

        const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

        const displayValue = typeof value === 'object' && value !== null
            ? JSON.stringify(value, null, 2)
            : value;

        return (
            <div key={resolvedKey} className="space-y-1">
                <div className="text-sm font-medium text-gray-700">{label}</div>
                {isEditable ? (
                    resolvedKey === 'Role' ? (
                        <select
                            className={inputClassName}
                            value={value as string}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, [resolvedKey]: e.target.value }))}
                        >
                            <option value="admin">admin</option>
                            <option value="user">user</option>
                        </select>
                    ) : (
                        <input
                            className={inputClassName}
                            value={(value as string) ?? ''}
                            onChange={(e) => setEditValues((prev) => ({ ...prev, [resolvedKey]: e.target.value }))}
                        />
                    )
                ) : (
                    <div className="px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-800 break-words">
                        {displayValue !== undefined && displayValue !== null ? displayValue : '未設定'}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">{error}</div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800">ユーザー詳細</h1>
                    <p className="text-sm text-gray-600 mt-1">ID: {id}</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        戻る
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-60"
                    >
                        {saving ? '保存中...' : '変更を保存'}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        {LEFT_FIELDS.map((field) => renderField(field))}
                    </div>
                    <div className="space-y-4">
                        {RIGHT_FIELDS.map((field) => renderField(field))}
                    </div>
                </div>
                <div className="mt-6 border-t pt-6">
                    <div className="text-base font-semibold text-gray-800 mb-4">補足住所</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {SUPPLEMENTAL_FIELDS.map((field) => renderField(field))}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800">関連する注文</h2>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">UserRef が一致する Order</span>
                        <button
                            onClick={() => setIsAddingOrder(true)}
                            className="px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm font-semibold"
                        >
                            ＋ Order追加
                        </button>
                    </div>
                </div>

                {orders.length === 0 ? (
                    <div className="text-gray-600">関連する注文はありません</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">資格名</th>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">ステータス</th>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">注文番号</th>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">注文日時</th>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">金額</th>
                                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">操作</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {orders.map((order) => {
                                    const normalizedOrder = normalizeValue(order);
                                    return (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-800">{order.shikakuName || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-800">{normalizedOrder.status || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-800">{normalizedOrder.orderNumber || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-800">{normalizedOrder.createdAt || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-800">{normalizedOrder.totalAmount ?? '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <Link to={`/admin/orders/${order.id}`} className="text-emerald-600 font-semibold hover:underline">
                                                    詳細
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>


            {isAddingOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">Order追加</h2>
                            <p className="text-sm text-gray-600 mt-1">ユーザーと資格を紐付けます</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ユーザー <span className="text-red-500">*</span>
                                </label>
                                <input
                                    value={userData?.display_name ? `${userData.display_name} (${id})` : id || ''}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    資格 <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newOrderForm.shikakuId}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, shikakuId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                >
                                    <option value="">選択してください</option>
                                    {shikakuList.map((shikaku) => (
                                        <option key={shikaku.id} value={shikaku.id}>
                                            {shikaku.ShikakuName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
                                <select
                                    value={newOrderForm.status}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, status: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                >
                                    <option value="active">有効</option>
                                    <option value="cancel">キャンセル</option>
                                    <option value="expired">期限切れ</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">コース</label>
                                <select
                                    value={newOrderForm.course}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, course: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                >
                                    <option value="通信講座＋試験">通信講座＋試験</option>
                                    <option value="通信講座のみ">通信講座のみ</option>
                                    <option value="試験のみ">試験のみ</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">認定証</label>
                                <select
                                    value={newOrderForm.diploma}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, diploma: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                >
                                    <option value="通常版">通常版</option>
                                    <option value="英語版">英語版</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">支払方法</label>
                                <select
                                    value={newOrderForm.paymentMethod}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, paymentMethod: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                >
                                    <option value="クレジット">クレジット</option>
                                    <option value="銀行振込">銀行振込</option>
                                    <option value="現金">現金</option>
                                    <option value="無料">無料</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">金額</label>
                                <input
                                    type="number"
                                    value={newOrderForm.totalAmount}
                                    onChange={(e) => setNewOrderForm({ ...newOrderForm, totalAmount: Number(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    min="0"
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={newOrderForm.isExamFinished}
                                        onChange={(e) => setNewOrderForm({ ...newOrderForm, isExamFinished: e.target.checked })}
                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                    />
                                    受験済みとして登録
                                </label>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm text-blue-700">
                                    ※ 注文番号は自動生成されます
                                    <br />
                                    ※ 申込日は現在日時、有効期限は2年後に自動設定されます
                                </p>
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setIsAddingOrder(false);
                                    resetOrderForm();
                                }}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleAddOrder}
                                disabled={addingOrder}
                                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-60"
                            >
                                {addingOrder ? '追加中...' : '追加'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
